<template>
  <div v-if="showControls" class="flex items-center gap-2">
    <!-- Background Color Picker -->
    <div class="relative">
      <button
        @click="$refs.bgColorPicker.click()"
        class="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        aria-label="Mudar cor de fundo"
      >
        <svg
          class="w-5 h-5 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
          />
        </svg>
      </button>
      <input
        ref="bgColorPicker"
        type="color"
        v-model="selectedBgColor"
        @input="handleBgColorChange"
        class="absolute opacity-0 pointer-events-none"
      />
    </div>

    <!-- Button Color Picker -->
    <div class="relative">
      <button
        @click="$refs.btnColorPicker.click()"
        class="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        aria-label="Mudar cor dos botões"
      >
        <svg
          class="w-5 h-5 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
          />
        </svg>
      </button>
      <input
        ref="btnColorPicker"
        type="color"
        v-model="selectedButtonColor"
        @input="handleButtonColorChange"
        class="absolute opacity-0 pointer-events-none"
      />
    </div>

    <!-- Gradient Type Selector -->
    <div class="relative">
      <button
        @click="showGradientTypeMenu = !showGradientTypeMenu"
        class="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        aria-label="Tipo de gradiente"
      >
        <svg
          class="w-5 h-5 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16m-7 6h7"
          />
        </svg>
      </button>
      <!-- Dropdown Menu -->
      <div
        v-if="showGradientTypeMenu"
        class="absolute top-full right-0 mt-2 w-48 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-lg overflow-hidden z-50"
      >
        <button
          v-for="type in gradientTypes"
          :key="type.id"
          @click="selectGradientType(type.id)"
          class="w-full px-4 py-3 text-left text-white hover:bg-white/20 transition-colors flex items-center gap-2"
          :class="{ 'bg-white/15': selectedGradientType === type.id }"
        >
          <span class="text-lg">{{ type.icon }}</span>
          <span class="text-sm font-medium">{{ type.name }}</span>
        </button>
      </div>
    </div>

    <!-- Gradient Preset Cycler -->
    <div class="relative">
      <button
        @click="cycleGradient"
        class="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        aria-label="Mudar gradiente"
      >
        <svg
          class="w-5 h-5 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      </button>
    </div>

    <!-- Image Upload Menu -->
    <div class="relative">
      <button
        @click="showImageUploadMenu = !showImageUploadMenu"
        class="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        aria-label="Upload de imagem"
      >
        <svg
          class="w-5 h-5 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </button>

      <!-- Upload Menu Dropdown -->
      <div
        v-if="showImageUploadMenu"
        class="absolute top-full right-0 mt-2 w-56 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-lg overflow-hidden z-50"
      >
        <div class="p-2 space-y-1">
          <button
            @click="openImageUpload('mobile')"
            class="w-full px-4 py-3 text-left text-white hover:bg-white/20 transition-colors rounded-lg flex items-center justify-between"
          >
            <div class="flex items-center gap-2">
              <span class="text-lg">📱</span>
              <span class="text-sm font-medium">Mobile</span>
            </div>
            <span class="text-xs text-white/60">480x854</span>
          </button>

          <button
            @click="openImageUpload('tablet')"
            class="w-full px-4 py-3 text-left text-white hover:bg-white/20 transition-colors rounded-lg flex items-center justify-between"
          >
            <div class="flex items-center gap-2">
              <span class="text-lg">💻</span>
              <span class="text-sm font-medium">Tablet</span>
            </div>
            <span class="text-xs text-white/60">1024x768</span>
          </button>

          <button
            @click="openImageUpload('desktop')"
            class="w-full px-4 py-3 text-left text-white hover:bg-white/20 transition-colors rounded-lg flex items-center justify-between"
          >
            <div class="flex items-center gap-2">
              <span class="text-lg">🖥️</span>
              <span class="text-sm font-medium">Desktop</span>
            </div>
            <span class="text-xs text-white/60">1920x1080</span>
          </button>

          <div v-if="hasCustomImages" class="pt-2 border-t border-white/20">
            <button
              @click="clearCustomImages"
              class="w-full px-4 py-2 text-left text-red-300 hover:bg-red-500/20 transition-colors rounded-lg flex items-center gap-2 text-sm"
            >
              <span>🗑️</span>
              <span>Limpar imagens</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Hidden file inputs -->
      <input
        ref="mobileImageInput"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        class="hidden"
        @change="handleImageSelect($event, 'mobile')"
      />
      <input
        ref="tabletImageInput"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        class="hidden"
        @change="handleImageSelect($event, 'tablet')"
      />
      <input
        ref="desktopImageInput"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        class="hidden"
        @change="handleImageSelect($event, 'desktop')"
      />
    </div>

    <!-- Form Styling Controls -->
    <div class="relative">
      <button
        @click="showFormStylingMenu = !showFormStylingMenu"
        class="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        aria-label="Estilização de formulários"
      >
        <svg
          class="w-5 h-5 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
          />
        </svg>
      </button>

      <!-- Form Styling Dropdown -->
      <div
        v-if="showFormStylingMenu"
        class="absolute top-full right-0 mt-2 w-80 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-lg overflow-hidden z-50 max-h-[600px] overflow-y-auto"
      >
        <div class="p-4 space-y-4">
          <div class="text-white font-semibold text-sm mb-3">
            🎯 Estilo do Formulário
          </div>

          <!-- Grouped Config Panel -->
          <GroupedConfigPanel
            :groups="configGroups"
            :saved-templates="savedTemplates"
            @toggle-group-enabled="toggleConfigGroupEnabled"
            @update-group-value="updateGroupValue"
            @apply-group="applyGroup"
            @apply-all-groups="applyAllConfigGroups"
            @detect-patterns="detectConfigPatterns"
            @save-template="openSaveTemplateModal"
            @load-template="openLoadTemplateModal"
            @load-template-direct="loadTemplate"
            @import-template="importTemplate"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Image Cropper Modal -->
  <ImageCropperModal
    v-model:visible="showCropperModal"
    :image-url="tempImageUrl"
    :device="currentDevice"
    @cropped="handleCroppedImage"
  />

  <!-- Template Management Modal -->
  <div
    v-if="showTemplateModal"
    class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    @click.self="showTemplateModal = false"
  >
    <div
      class="bg-slate-800 rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden border border-white/10"
    >
      <!-- Modal Header -->
      <div
        class="px-6 py-4 border-b border-white/10 flex items-center justify-between"
      >
        <h3 class="text-white font-semibold text-lg">
          {{
            templateAction === "save"
              ? "💾 Salvar Template"
              : "📂 Carregar Template"
          }}
        </h3>
        <button
          @click="showTemplateModal = false"
          class="text-white/50 hover:text-white transition-colors"
        >
          ✕
        </button>
      </div>

      <!-- Modal Content -->
      <div class="p-6 overflow-y-auto max-h-[60vh]">
        <!-- Save Template Form -->
        <div v-if="templateAction === 'save'" class="space-y-4">
          <div>
            <label class="block text-white text-sm font-medium mb-2"
              >Nome do Template</label
            >
            <input
              v-model="newTemplateName"
              type="text"
              placeholder="Ex: Cliente ABC - Configuração Padrão"
              class="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-blue-500"
              @keyup.enter="saveTemplate"
            />
          </div>

          <div class="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <div class="text-blue-300 text-xs font-medium mb-2">
              📋 O que será salvo:
            </div>
            <ul class="text-blue-200 text-xs space-y-1">
              <li>✓ Configurações de todos os 7 grupos</li>
              <li>✓ Estados enabled/disabled de cada grupo</li>
              <li>✓ Dispositivos e modos aplicados</li>
              <li>✓ Todas as cores, tamanhos e textos</li>
            </ul>
          </div>

          <div class="flex gap-2">
            <button
              @click="showTemplateModal = false"
              class="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              @click="saveTemplate"
              class="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
            >
              💾 Salvar
            </button>
          </div>
        </div>

        <!-- Load Template List -->
        <div v-else class="space-y-3">
          <div v-if="savedTemplates.length === 0" class="text-center py-8">
            <div class="text-white/50 text-sm mb-2">📦</div>
            <div class="text-white/50 text-sm">Nenhum template salvo ainda</div>
          </div>

          <div v-else class="space-y-2">
            <div
              v-for="template in savedTemplates"
              :key="template.id"
              class="p-4 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 transition-colors"
            >
              <div class="flex items-start justify-between mb-2">
                <div class="flex-1 min-w-0">
                  <div class="text-white font-medium text-sm mb-1">
                    {{ template.name }}
                  </div>
                  <div class="text-white/50 text-xs">
                    {{ formatDate(template.createdAt) }}
                  </div>
                </div>
                <div
                  class="w-6 h-6 rounded border-2 border-white/20"
                  :style="{
                    backgroundColor:
                      template.preview?.primaryColor || '#3b82f6',
                  }"
                  :title="`Cor principal: ${template.preview?.primaryColor}`"
                ></div>
              </div>

              <div class="flex gap-1 text-[10px] mb-3">
                <span
                  v-if="template.preview?.globalColorsEnabled"
                  class="px-2 py-1 bg-blue-500/20 text-blue-300 rounded"
                >
                  🎨 Cores
                </span>
                <span
                  v-if="template.preview?.darkModeEnabled"
                  class="px-2 py-1 bg-slate-700/50 text-slate-300 rounded"
                >
                  🌙 Dark
                </span>
                <span
                  v-if="template.preview?.lightModeEnabled"
                  class="px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded"
                >
                  ☀️ Light
                </span>
              </div>

              <div class="flex gap-2">
                <button
                  @click="loadTemplate(template)"
                  class="flex-1 px-3 py-1.5 bg-green-500/80 hover:bg-green-500 text-white text-xs font-medium rounded transition-colors"
                >
                  📥 Carregar
                </button>
                <button
                  @click="exportTemplate(template)"
                  class="px-3 py-1.5 bg-purple-500/80 hover:bg-purple-500 text-white text-xs rounded transition-colors"
                  title="Exportar como JSON"
                >
                  📤
                </button>
                <button
                  @click="deleteTemplate(template.id)"
                  class="px-3 py-1.5 bg-red-500/80 hover:bg-red-500 text-white text-xs rounded transition-colors"
                  title="Deletar template"
                >
                  🗑️
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import backgroundConfigService from "@/services/backgroundConfigService";
import { formStylingPreferences } from "@/services/formStylingPreferences";
import ImageCropperModal from "./ImageCropperModal.vue";
import GroupedConfigPanel from "./GroupedConfigPanel.vue";
import { useTheme } from "@/composables/useTheme";

export default {
  name: "BackgroundCustomizer",
  components: {
    ImageCropperModal,
    GroupedConfigPanel,
  },
  setup() {
    const { isDark } = useTheme();
    return { isDark };
  },
  props: {
    nomeEmpresa: {
      type: String,
      required: true,
    },
    showControls: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectedBgColor: "#00235C",
      selectedButtonColor: "#3b82f6",
      selectedGradient: "default",
      selectedGradientType: "linear",
      showGradientTypeMenu: false,
      showImageUploadMenu: false,
      showFormStylingMenu: false,
      showCropperModal: false,
      tempImageUrl: null,
      currentDevice: "mobile",
      customImages: {
        mobile: null,
        tablet: null,
        desktop: null,
      },
      formStyling: {
        enabled: {
          mobile: false,
          tablet: false,
          desktop: false,
        },
        currentEditingDevice: "mobile",
        mobile: {
          mode: "dark",
          width: "100%",
          minWidth: "0px",
          maxWidth: "600px",
          backgroundColor: "#FFFFFF",
          opacity: 10,
          alignment: "center",
          alignmentOffset: 6,
          logoInvert: false,
          textColor: "#FFFFFF",
          inputBgEnabled: "#FFFFFF",
          inputBgDisabled: "#F3F4F6",
          inputTextColor: "#000000",
          buttonTextColor: "#FFFFFF",
          buttonHoverColor: "#2563eb",
          buttonColorType1: "#3b82f6",
          buttonOpacityType1: 100,
          buttonColorType2: "#FFFFFF",
          buttonOpacityType2: 20,
          customTexts: {
            preLoginTitle: "",
            preLoginSubtitle: "",
            formTitle: "",
            formSubtitle: "",
            tokenTitle: "",
            tokenSubtitle: "",
            recoverTitle: "",
            recoverSubtitle: "",
            cadastroTitle: "",
            cadastroSubtitle: "",
          },
        },
        tablet: {
          mode: "dark",
          width: "100%",
          minWidth: "0px",
          maxWidth: "800px",
          backgroundColor: "#FFFFFF",
          opacity: 10,
          alignment: "center",
          alignmentOffset: 12,
          logoInvert: false,
          textColor: "#FFFFFF",
          inputBgEnabled: "#FFFFFF",
          inputBgDisabled: "#F3F4F6",
          inputTextColor: "#000000",
          buttonTextColor: "#FFFFFF",
          buttonHoverColor: "#2563eb",
          buttonColorType1: "#3b82f6",
          buttonOpacityType1: 100,
          buttonColorType2: "#FFFFFF",
          buttonOpacityType2: 20,
          customTexts: {
            preLoginTitle: "",
            preLoginSubtitle: "",
            formTitle: "",
            formSubtitle: "",
            tokenTitle: "",
            tokenSubtitle: "",
            recoverTitle: "",
            recoverSubtitle: "",
            cadastroTitle: "",
            cadastroSubtitle: "",
          },
        },
        desktop: {
          mode: "dark",
          width: "100%",
          minWidth: "500px",
          maxWidth: "1000px",
          margin: "0rem",
          backgroundColor: "#FFFFFF",
          opacity: 10,
          alignment: "center",
          alignmentOffset: 16,
          logoInvert: false,
          textColor: "#FFFFFF",
          inputBgEnabled: "#FFFFFF",
          inputBgDisabled: "#F3F4F6",
          inputTextColor: "#000000",
          buttonTextColor: "#FFFFFF",
          buttonHoverColor: "#2563eb",
          buttonColorType1: "#3b82f6",
          buttonOpacityType1: 100,
          buttonColorType2: "#FFFFFF",
          buttonOpacityType2: 20,
          customTexts: {
            preLoginTitle: "",
            preLoginSubtitle: "",
            formTitle: "",
            formSubtitle: "",
            tokenTitle: "",
            tokenSubtitle: "",
            recoverTitle: "",
            recoverSubtitle: "",
            cadastroTitle: "",
            cadastroSubtitle: "",
          },
        },
      },
      // Sistema de Grupos de Configuração
      configGroups: {
        globalTexts: {
          name: "Textos Customizados",
          icon: "📝",
          enabled: false,
          applyTo: {
            devices: ["mobile", "tablet", "desktop"],
            modes: ["dark", "light"],
          },
          values: {
            customTexts: {
              preLoginTitle: "Olá, Seja Bem-vindo!",
              preLoginSubtitle: "Gerencie suas operações com segurança",
              formTitle: "Acesse Sua Conta",
              formSubtitle: "Insira seus dados para continuar.",
              tokenTitle: "Chave de acesso",
              tokenSubtitle:
                "Para continuar, digite o código que acabamos de enviar para o seu e-mail.",
              recoverTitle: "Recuperar senha",
              recoverSubtitle: "Digite seus dados para recuperar o acesso.",
              cadastroTitle: "Cadastre-se",
              cadastroSubtitle: "Preencha seus dados para criar sua conta.",
            },
          },
        },
        mobileLayout: {
          name: "Layout Mobile",
          icon: "📱",
          enabled: true,
          applyTo: {
            devices: ["mobile"],
            modes: ["dark", "light"],
          },
          values: {
            width: "100%",
            minWidth: "0px",
            maxWidth: "600px",
            alignment: "center",
            alignmentOffset: 6,
          },
        },
        tabletLayout: {
          name: "Layout Tablet",
          icon: "💻",
          enabled: true,
          applyTo: {
            devices: ["tablet"],
            modes: ["dark", "light"],
          },
          values: {
            width: "100%",
            minWidth: "0px",
            maxWidth: "800px",
            alignment: "center",
            alignmentOffset: 12,
          },
        },
        desktopLayout: {
          name: "Layout Desktop",
          icon: "🖥️",
          enabled: true,
          applyTo: {
            devices: ["desktop"],
            modes: ["dark", "light"],
          },
          values: {
            width: "1000px",
            minWidth: "400px",
            maxWidth: "1000px",
            alignment: "center",
            alignmentOffset: 16,
            margin: "0rem",
          },
        },
        darkMode: {
          name: "Aparência Dark",
          icon: "🌙",
          enabled: true,
          applyTo: {
            devices: ["mobile", "tablet", "desktop"],
            modes: ["dark"],
          },
          values: {
            mode: "dark",
            backgroundColor: "#1E293B",
            opacity: 15,
            logoInvert: true,
            buttonColorType1: "#3b82f6",
            buttonOpacityType1: 100,
            buttonColorType2: "#FFFFFF",
            buttonOpacityType2: 20,
            buttonTextColor: "#FFFFFF",
            buttonHoverColor: "#2563eb",
            textColor: "#FFFFFF",
            inputTextColor: "#000000",
            inputBgEnabled: "#FFFFFF",
            inputBgDisabled: "#F3F4F6",
          },
        },
        lightMode: {
          name: "Aparência Light",
          icon: "☀️",
          enabled: true,
          applyTo: {
            devices: ["mobile", "tablet", "desktop"],
            modes: ["light"],
          },
          values: {
            mode: "light",
            backgroundColor: "#FFFFFF",
            opacity: 90,
            logoInvert: false,
            buttonColorType1: "#3b82f6",
            buttonOpacityType1: 100,
            buttonColorType2: "#FFFFFF",
            buttonOpacityType2: 20,
            buttonTextColor: "#FFFFFF",
            buttonHoverColor: "#1d4ed8",
            textColor: "#000000",
            inputTextColor: "#000000",
            inputBgEnabled: "#FFFFFF",
            inputBgDisabled: "#F3F4F6",
          },
        },
      },
      savedTemplates: [], // Templates salvos (carregados do localStorage)
      showTemplateModal: false,
      templateAction: "save", // 'save' ou 'load'
      newTemplateName: "",
      selectedTemplate: null,
      imageDimensions: {
        mobile: { width: 480, height: 854, tolerance: 50 },
        tablet: { width: 1024, height: 768, tolerance: 100 },
        desktop: { width: 1920, height: 1080, tolerance: 150 },
      },
      gradientTypes: [
        { id: "linear", name: "Linear Gradient", icon: "↗️" },
        { id: "radial", name: "Radial Gradient", icon: "⭕" },
        { id: "mesh", name: "Mesh Gradient", icon: "🔷" },
        { id: "noise", name: "Noise Gradient", icon: "🌀" },
        { id: "blur", name: "Blur Gradient", icon: "💫" },
      ],
      gradients: [
        { name: "default", from: "#00235C", via: "#003875", to: "#00509E" },
        { name: "purple", from: "#6B21A8", via: "#7C3AED", to: "#A855F7" },
        { name: "green", from: "#065F46", via: "#047857", to: "#059669" },
        { name: "orange", from: "#9A3412", via: "#C2410C", to: "#EA580C" },
        { name: "pink", from: "#9F1239", via: "#BE123C", to: "#E11D48" },
      ],
      configLoaded: false,
      isInitialLoading: true, // Previne emissões durante carregamento inicial
      saveTimeout: null,
    };
  },
  async mounted() {
    await this.loadBackgroundConfig();
    this.loadSavedTemplates();
    // currentEditingDevice é estado de UI, não de config persistida:
    // sempre inicializa com o dispositivo do viewport, não com o que
    // veio do servidor (caso contrário, abrir de desktop mostra "mobile").
    this.formStyling.currentEditingDevice = this.detectedDevice;
  },
  computed: {
    apiBaseUrl() {
      return process.env.VUE_APP_API_URL || process.env.VUE_APP_TITLE || "";
    },
    hasCustomImages() {
      return (
        this.customImages.mobile ||
        this.customImages.tablet ||
        this.customImages.desktop
      );
    },
    detectedDevice() {
      const width = window.innerWidth;
      if (width <= 768) return "mobile";
      if (width <= 1024) return "tablet";
      return "desktop";
    },
    currentDeviceConfig() {
      const device = this.formStyling?.currentEditingDevice || "mobile";
      return (
        this.formStyling?.[device] || {
          mode: "dark",
          minWidth: "0%",
          maxWidth: "100%",
          backgroundColor: "#FFFFFF",
          opacity: 10,
          alignment: "center",
          alignmentOffset: 6,
          logoInvert: false,
          textColor: "#FFFFFF",
          inputBgEnabled: "#FFFFFF",
          inputBgDisabled: "#F3F4F6",
          inputTextColor: "#000000",
          buttonTextColor: "#FFFFFF",
        }
      );
    },
    widthValue: {
      get() {
        const width = this.currentDeviceConfig.width;
        return parseInt(width) || 100;
      },
      set(value) {
        const device = this.formStyling.currentEditingDevice;
        if (this.formStyling[device]) {
          this.formStyling[device].width = `${value}%`;
        }
      },
    },
    minWidthValue: {
      get() {
        const minWidth = this.currentDeviceConfig.minWidth;
        return parseInt(minWidth) || 0;
      },
      set(value) {
        const device = this.formStyling.currentEditingDevice;
        if (this.formStyling[device]) {
          this.formStyling[device].minWidth = `${value}px`;
        }
      },
    },
    maxWidthValue: {
      get() {
        const maxWidth = this.currentDeviceConfig.maxWidth;
        return parseInt(maxWidth) || 600;
      },
      set(value) {
        const device = this.formStyling.currentEditingDevice;
        if (this.formStyling[device]) {
          this.formStyling[device].maxWidth = `${value}px`;
        }
      },
    },
    currentGradient() {
      return (
        this.gradients.find((g) => g.name === this.selectedGradient) ||
        this.gradients[0]
      );
    },
    dynamicBackgroundStyle() {
      // Se houver imagem customizada para o dispositivo atual, usa ela
      const deviceImage = this.customImages[this.detectedDevice];
      if (deviceImage) {
        // Se for data URL (base64), usa direto; senão, monta URL completa com API
        const imageUrl = deviceImage.startsWith("data:")
          ? deviceImage
          : `${this.apiBaseUrl}${deviceImage}`;

        return {
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        };
      }

      // Caso contrário, usa o gradiente
      const { from, via, to } = this.currentGradient;

      let backgroundStyle = "";

      switch (this.selectedGradientType) {
        case "linear":
          backgroundStyle = `linear-gradient(135deg, ${from}, ${via}, ${to})`;
          break;
        case "radial":
          backgroundStyle = `radial-gradient(circle at center, ${from}, ${via}, ${to})`;
          break;
        case "mesh":
          backgroundStyle = `
            radial-gradient(circle at 20% 30%, ${from} 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, ${via} 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, ${to} 0%, transparent 50%),
            linear-gradient(135deg, ${from}, ${to})
          `;
          break;
        case "noise":
          backgroundStyle = `
            repeating-linear-gradient(45deg, ${from} 0px, ${via} 10px, ${to} 20px, ${from} 30px),
            linear-gradient(135deg, ${from}, ${to})
          `;
          break;
        case "blur":
          backgroundStyle = `radial-gradient(ellipse at 30% 30%, ${from} 0%, ${via} 40%, ${to} 100%)`;
          break;
        default:
          backgroundStyle = `linear-gradient(135deg, ${from}, ${via}, ${to})`;
      }

      return { background: backgroundStyle };
    },
  },
  watch: {
    customImages: {
      handler() {
        // Quando imagens customizadas mudam, força atualização do background
        // Bloqueia durante carregamento inicial para evitar flash
        if (this.configLoaded && !this.isInitialLoading) {
          this.$nextTick(() => {
            this.emitBackgroundChange(this.dynamicBackgroundStyle);
          });
        }
      },
      deep: true,
    },
    dynamicBackgroundStyle: {
      handler(newStyle) {
        // Só emite após carregar a configuração inicial E pré-carregar imagens
        // Bloqueia durante carregamento inicial para evitar flash
        if (this.configLoaded && !this.isInitialLoading) {
          this.emitBackgroundChange(newStyle);
        }
      },
      deep: true,
    },
    selectedButtonColor(newColor) {
      // Emite mudança de cor de botão em tempo real
      if (this.configLoaded) {
        this.$emit("button-color-change", newColor);
      }
    },
    configLoaded(newVal) {
      // Quando terminar de carregar, emite o estilo atual
      if (newVal) {
        this.$nextTick(async () => {
          const backgroundStyle = this.dynamicBackgroundStyle;
          const hasBackgroundImage =
            backgroundStyle?.background?.includes("url(");

          if (hasBackgroundImage) {
            // Pré-carrega imagens ANTES de emitir
            await this.emitBackgroundChange(backgroundStyle);
          } else {
            // Sem imagens, emite diretamente sem delay
            this.$emit("background-change", backgroundStyle);
          }

          this.$emit("button-color-change", this.selectedButtonColor);
          // O carregamento preserva valores e enabled exatamente como vieram da API.
          this.emitFormStyling({ recalculateEnabled: false });

          // Agora que tudo foi emitido, libera os watchers
          this.isInitialLoading = false;
        });
      }
    },
    configGroups: {
      handler() {
        // Salva quando grupos mudam
        if (this.configLoaded) {
          this.saveGroupPreferences();
        }
      },
      deep: true,
    },
  },
  methods: {
    // Converte customTexts do formato antigo {enabled, content} para strings diretas
    convertCustomTextsToStrings(customTexts) {
      if (!customTexts) return {};

      const converted = {};
      for (const key in customTexts) {
        const value = customTexts[key];
        // Se for objeto com 'content', extrai a string
        if (value && typeof value === "object" && "content" in value) {
          converted[key] = value.content || "";
        } else if (typeof value === "string") {
          // Se já for string, mantém
          converted[key] = value;
        } else {
          converted[key] = "";
        }
      }
      return converted;
    },

    // Converte customTexts de strings diretas para formato {enabled, content} do backend
    convertCustomTextsToObjects(customTexts) {
      if (!customTexts) return {};

      const converted = {};
      for (const key in customTexts) {
        const value = customTexts[key];
        // Se for string, converte para objeto
        if (typeof value === "string") {
          converted[key] = {
            enabled: value !== "", // enabled se não for vazio
            content: value,
          };
        } else if (value && typeof value === "object" && "content" in value) {
          // Se já for objeto, mantém
          converted[key] = value;
        } else {
          converted[key] = { enabled: false, content: "" };
        }
      }
      return converted;
    },

    async loadBackgroundConfig() {
      try {
        const config = await backgroundConfigService.loadConfig();

        this.selectedBgColor = config.selectedBgColor || "#00235C";
        this.selectedButtonColor = config.selectedButtonColor || "#3b82f6";
        this.selectedGradient = config.selectedGradient || "default";
        this.selectedGradientType = config.selectedGradientType || "linear";

        // Load form styling config - merge with defaults
        if (config.formStyling) {
          const defaultDeviceConfig = {
            mode: "dark",
            width: "100%",
            minWidth: "0px",
            maxWidth: "600px",
            margin: "0rem",
            backgroundColor: "#FFFFFF",
            opacity: 10,
            alignment: "center",
            alignmentOffset: 6,
            logoInvert: false,
            textColor: "#FFFFFF",
            inputBgEnabled: "#FFFFFF",
            inputBgDisabled: "#F3F4F6",
            inputTextColor: "#000000",
            buttonTextColor: "#FFFFFF",
            buttonHoverColor: "#2563eb",
            buttonColorType1: "#3b82f6",
            buttonOpacityType1: 100,
            buttonColorType2: "#FFFFFF",
            buttonOpacityType2: 20,
            customTexts: {
              preLoginTitle: "",
              preLoginSubtitle: "",
              formTitle: "",
              formSubtitle: "",
              tokenTitle: "",
              tokenSubtitle: "",
              recoverTitle: "",
              recoverSubtitle: "",
              cadastroTitle: "",
              cadastroSubtitle: "",
            },
          };

          // Converte customTexts ANTES do merge para evitar que objetos sobrescrevam strings
          const mobileFromBackend = { ...config.formStyling.mobile };
          const tabletFromBackend = { ...config.formStyling.tablet };
          const desktopFromBackend = { ...config.formStyling.desktop };

          // Converte customTexts do backend para strings diretas
          if (mobileFromBackend.customTexts) {
            mobileFromBackend.customTexts = this.convertCustomTextsToStrings(
              mobileFromBackend.customTexts,
            );
          }
          if (tabletFromBackend.customTexts) {
            tabletFromBackend.customTexts = this.convertCustomTextsToStrings(
              tabletFromBackend.customTexts,
            );
          }
          if (desktopFromBackend.customTexts) {
            desktopFromBackend.customTexts = this.convertCustomTextsToStrings(
              desktopFromBackend.customTexts,
            );
          }

          // Agora faz o merge com os defaults (customTexts já convertidos)
          const mobileConfig = { ...defaultDeviceConfig, ...mobileFromBackend };
          const tabletConfig = { ...defaultDeviceConfig, ...tabletFromBackend };
          const desktopConfig = {
            ...defaultDeviceConfig,
            minWidth: "500px",
            maxWidth: "1000px",
            ...desktopFromBackend,
          };

          this.formStyling = {
            enabled: config.formStyling.enabled || this.formStyling.enabled,
            // currentEditingDevice é sobrescrito em mounted() com base no
            // viewport real; não usar o valor persistido pelo servidor.
            currentEditingDevice: "mobile",
            mobile: mobileConfig,
            tablet: tabletConfig,
            desktop: desktopConfig,
          };

          // Detecta padrões automaticamente ao carregar
          this.detectConfigPatterns();
        }

        // Carrega preferências de grupos do localStorage
        this.loadGroupPreferences();

        // Sincroniza textos do grupo globalTexts com os valores do servidor
        // para garantir que o painel de edição mostre os textos reais salvos
        if (this.configGroups.globalTexts?.values?.customTexts) {
          const serverTexts = this.formStyling.desktop?.customTexts || 
                              this.formStyling.mobile?.customTexts || {};
          Object.keys(serverTexts).forEach((key) => {
            if (serverTexts[key] && this.configGroups.globalTexts.values.customTexts[key] !== undefined) {
              this.configGroups.globalTexts.values.customTexts[key] = serverTexts[key];
            }
          });
        }

        // Grupos só são aplicados após uma ação explícita do usuário.
        // Reaplicá-los aqui sobrescreveria a configuração recebida por dispositivo.

        // Atualiza o gradiente com a cor salva
        const currentGrad = this.gradients.find(
          (g) => g.name === this.selectedGradient,
        );
        if (currentGrad) {
          const updatedGradient = {
            ...currentGrad,
            from: this.selectedBgColor,
          };
          const index = this.gradients.findIndex(
            (g) => g.name === this.selectedGradient,
          );
          if (index !== -1) {
            this.gradients[index] = updatedGradient;
          }
        }

        // Carrega imagens customizadas do servidor
        const images = await backgroundConfigService.getImages();
        if (images && images.data) {
          const { mobile, tablet, desktop } = images.data;
          if (mobile) this.customImages.mobile = mobile;
          if (tablet) this.customImages.tablet = tablet;
          if (desktop) this.customImages.desktop = desktop;
        }

      } catch (error) {
        // Garante valores padrão em caso de erro crítico
        this.selectedBgColor = "#00235C";
        this.selectedButtonColor = "#3b82f6";
        this.selectedGradient = "default";
        this.selectedGradientType = "linear";
      } finally {
        // SEMPRE marca como carregado, independente de sucesso ou erro
        this.configLoaded = true;
      }
    },
    saveBackgroundConfig() {
      if (!this.configLoaded) {
        return;
      }

      // Cancela o timeout anterior se existir (debounce)
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }

      // Cria novo timeout para salvar após 5 segundos
      this.saveTimeout = setTimeout(async () => {
        // Prepara formStyling para enviar ao backend convertendo customTexts.
        // currentEditingDevice é estado de UI (qual aba está aberta no painel),
        // não deve ser persistido — senão o backend sempre devolve o último
        // dispositivo editado em vez do dispositivo real do usuário.
        const formStylingToSave = {
          ...this.formStyling,
          mobile: {
            ...this.formStyling.mobile,
            customTexts: this.convertCustomTextsToObjects(
              this.formStyling.mobile.customTexts,
            ),
          },
          tablet: {
            ...this.formStyling.tablet,
            customTexts: this.convertCustomTextsToObjects(
              this.formStyling.tablet.customTexts,
            ),
          },
          desktop: {
            ...this.formStyling.desktop,
            customTexts: this.convertCustomTextsToObjects(
              this.formStyling.desktop.customTexts,
            ),
          },
        };
        delete formStylingToSave.currentEditingDevice;

        const config = {
          selectedBgColor: this.selectedBgColor,
          selectedButtonColor: this.selectedButtonColor,
          selectedGradient: this.selectedGradient,
          selectedGradientType: this.selectedGradientType,
          formStyling: formStylingToSave,
          lastUpdated: new Date().toISOString(),
        };

        try {
          await backgroundConfigService.saveConfig(config);
        } catch (error) {
          // TODO: Mostrar notificação de erro para o usuário
        }
      }, 5000); // 5 segundos de debounce
    },
    handleBgColorChange() {
      const index = this.gradients.findIndex(
        (g) => g.name === this.selectedGradient,
      );
      if (index !== -1) {
        const currentGrad = this.gradients[index];
        // Vue 3 usa Proxy - atribuição direta é reativa
        this.gradients[index] = {
          ...currentGrad,
          from: this.selectedBgColor,
        };
      }
      // Debounce de 5s já implementado no saveBackgroundConfig
      this.saveBackgroundConfig();
    },
    handleButtonColorChange() {
      this.saveBackgroundConfig();
    },
    cycleGradient() {
      const currentIndex = this.gradients.findIndex(
        (g) => g.name === this.selectedGradient,
      );
      const nextIndex = (currentIndex + 1) % this.gradients.length;
      this.selectedGradient = this.gradients[nextIndex].name;
      this.selectedBgColor = this.gradients[nextIndex].from;
      this.saveBackgroundConfig();
    },
    selectGradientType(typeId) {
      this.selectedGradientType = typeId;
      this.showGradientTypeMenu = false;
      this.saveBackgroundConfig();
    },
    getAdaptiveButtonColor() {
      // Se o background é escuro, clareia o botão
      // Se o background é claro, escurece o botão
      const isLight = this.isLightColor(this.selectedBgColor);
      return isLight
        ? this.darkenColor(this.selectedBgColor, 30)
        : this.lightenColor(this.selectedBgColor, 40);
    },
    isLightColor(hex) {
      // Calcula luminância relativa (perceived brightness)
      hex = hex.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      // Fórmula de luminância relativa (ITU-R BT.709)
      const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

      // Threshold: acima de 0.5 é considerado claro
      return luminance > 0.5;
    },
    lightenColor(hex, percent) {
      // Remove o # se existir
      hex = hex.replace("#", "");

      // Converte para RGB
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      // Clareia cada componente
      const newR = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
      const newG = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
      const newB = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

      // Converte de volta para hex
      const toHex = (n) => {
        const hex = n.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      };

      return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
    },
    darkenColor(hex, percent) {
      hex = hex.replace("#", "");

      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      const newR = Math.max(0, Math.floor(r * (1 - percent / 100)));
      const newG = Math.max(0, Math.floor(g * (1 - percent / 100)));
      const newB = Math.max(0, Math.floor(b * (1 - percent / 100)));

      const toHex = (num) => {
        const hex = num.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      };

      return "#" + toHex(newR) + toHex(newG) + toHex(newB);
    },
    openImageUpload(device) {
      this.currentDevice = device;
      this.showImageUploadMenu = false;

      const inputRef = `${device}ImageInput`;
      if (this.$refs[inputRef]) {
        this.$refs[inputRef].click();
      }
    },
    async handleImageSelect(event, device) {
      const file = event.target.files[0];
      if (!file) return;

      // Valida tipo de arquivo
      if (!file.type.match(/image\/(jpeg|png|webp)/)) {
        this.$alert({
          icon: "error",
          title: "Formato inválido",
          text: "Por favor, selecione uma imagem JPG, PNG ou WebP.",
        });
        return;
      }

      // Valida tamanho máximo (10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.$alert({
          icon: "error",
          title: "Arquivo muito grande",
          text: "A imagem deve ter no máximo 10MB.",
        });
        return;
      }

      // Lê a imagem
      const reader = new FileReader();
      reader.onload = async (e) => {
        const img = new Image();
        img.onload = () => {
          const validation = this.validateImageDimensions(img, device);

          if (validation.perfect) {
            // Dimensões perfeitas - aplica direto
            this.customImages[device] = e.target.result;
            this.uploadCustomImage(device, file);
            this.$alert({
              icon: "success",
              title: "Imagem aplicada!",
              text: `Imagem para ${device} foi aplicada com sucesso.`,
            });
          } else if (validation.needsCrop) {
            // Precisa recortar - abre modal
            this.tempImageUrl = e.target.result;
            this.currentDevice = device;
            this.showCropperModal = true;
          } else {
            // Dimensões muito diferentes - mostra erro
            this.$alert({
              icon: "warning",
              title: "Dimensões incorretas",
              html: `
                <p>A imagem selecionada tem ${img.width}x${img.height}px.</p>
                <p class="mt-2"><strong>Dimensão recomendada para ${device}:</strong><br/>
                ${validation.recommended.width}x${validation.recommended.height}px</p>
                <p class="mt-2 text-sm">Por favor, selecione uma imagem com dimensões próximas ou use a ferramenta de recorte.</p>
              `,
              confirmButtonText: "Recortar",
              showCancelButton: true,
              cancelButtonText: "Cancelar",
            }).then((result) => {
              if (result.value) {
                this.tempImageUrl = e.target.result;
                this.currentDevice = device;
                this.showCropperModal = true;
              }
            });
          }
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);

      // Limpa o input para permitir selecionar o mesmo arquivo novamente
      event.target.value = "";
    },
    validateImageDimensions(img, device) {
      const dims = this.imageDimensions[device];
      const widthDiff = Math.abs(img.width - dims.width);
      const heightDiff = Math.abs(img.height - dims.height);

      // Dimensões perfeitas (exatas ou dentro da tolerância mínima)
      if (widthDiff <= 10 && heightDiff <= 10) {
        return {
          perfect: true,
          needsCrop: false,
          recommended: dims,
        };
      }

      // Dimensões próximas - oferece crop
      if (widthDiff <= dims.tolerance && heightDiff <= dims.tolerance) {
        return {
          perfect: false,
          needsCrop: true,
          recommended: dims,
        };
      }

      // Dimensões muito diferentes
      return {
        perfect: false,
        needsCrop: false,
        recommended: dims,
      };
    },
    async handleCroppedImage(data) {
      this.customImages[data.device] = data.dataUrl;

      // Upload da imagem recortada
      await this.uploadCustomImage(data.device, data.file);

      this.$alert({
        icon: "success",
        title: "Imagem aplicada!",
        text: `Imagem para ${data.device} foi recortada e aplicada com sucesso.`,
      });
    },
    async uploadCustomImage(device, file) {
      try {

        const formData = new FormData();
        formData.append("image", file);
        formData.append("device", device);

        await backgroundConfigService.uploadImage(formData);

      } catch (error) {
        this.$alert({
          icon: "error",
          title: "Erro no upload",
          text: "Não foi possível enviar a imagem. Tente novamente.",
        });
      }
    },
    clearCustomImages() {
      this.$alert({
        icon: "warning",
        title: "Limpar imagens customizadas?",
        text: "Todas as imagens personalizadas serão removidas e os gradientes padrão serão restaurados.",
        showCancelButton: true,
        confirmButtonText: "Sim, limpar",
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (result.value) {
          this.customImages = {
            mobile: null,
            tablet: null,
            desktop: null,
          };

          try {
            await backgroundConfigService.deleteImages();
            this.showImageUploadMenu = false;

            this.$alert({
              icon: "success",
              title: "Imagens removidas",
              text: "As imagens personalizadas foram removidas com sucesso.",
            });
          } catch (error) {
            // Falha ao remover imagens no servidor; imagens locais já foram limpas.
          }
        }
      });
    },
    emitFormStyling({ recalculateEnabled = true } = {}) {
      if (this.configLoaded) {
        // Recalcula enabled.{device} antes de emitir: o consumidor
        // (ex: CadastroInicio) usa esses flags para decidir se a
        // customização está ativa no dispositivo atual.
        if (recalculateEnabled) this.recalcEnabledByDevice();
        this.$emit("form-styling-change", this.formStyling);
        // Debug: mostra customTexts especificamente
      }
    },

    /**
     * Recalcula formStyling.enabled.{device} com base nos grupos ativos
     * que se aplicam ao device. Regra especial: o grupo de layout do
     * device ({device}Layout) é considerado a "âncora" da customização
     * daquele device — se ele estiver desligado, enabled[device] = false,
     * mesmo que outros grupos (darkMode, globalTexts, ...) cubram o device.
     *
     * Caso o {device}Layout esteja ligado (ou não exista), vale a regra
     * geral: enabled[device] = true se QUALQUER grupo ativo cobre o device.
     */
    recalcEnabledByDevice() {
      if (!this.formStyling?.enabled) {
        this.formStyling.enabled = { mobile: false, tablet: false, desktop: false };
      }
      const devices = ["mobile", "tablet", "desktop"];
      devices.forEach((device) => {
        const layoutGroup = this.configGroups?.[`${device}Layout`];
        // Se existe um grupo de layout específico para o device e ele está
        // desligado, o device fica desabilitado — sem customização de layout,
        // não há customização focada nesse device.
        if (layoutGroup && layoutGroup.enabled === false) {
          this.formStyling.enabled[device] = false;
          return;
        }
        // Caso contrário, habilitado se QUALQUER grupo ativo cobre o device.
        const hasActiveGroup = Object.values(this.configGroups || {}).some(
          (group) =>
            group?.enabled &&
            Array.isArray(group.applyTo?.devices) &&
            group.applyTo.devices.includes(device),
        );
        this.formStyling.enabled[device] = hasActiveGroup;
      });
    },

    /**
     * Pré-carrega todas as imagens de background presentes no estilo
     * Retorna uma promise que resolve quando todas as imagens estiverem carregadas
     */
    async preloadBackgroundImages(styleObject) {
      if (!styleObject || !styleObject.background) {
        return true;
      }

      const backgroundStyle = styleObject.background;

      // Extrai URLs de imagens do estilo usando regex
      const imageUrlRegex = /url\(['"]?([^'"()]+)['"]?\)/g;
      const matches = [...backgroundStyle.matchAll(imageUrlRegex)];
      const imageUrls = matches.map((match) => match[1]);

      if (imageUrls.length === 0) {
        return true;
      }


      // Cria promessas para carregar e decodificar cada imagem
      const loadPromises = imageUrls.map((url) => {
        return new Promise((resolve) => {
          const img = new Image();
          let timeoutId;

          // Timeout de 5 segundos para não travar indefinidamente
          timeoutId = setTimeout(() => {
            resolve();
          }, 5000);

          // Define crossOrigin se necessário para imagens externas
          if (url.startsWith("http")) {
            img.crossOrigin = "anonymous";
          }

          img.onload = async () => {
            try {
              // Aguarda a decodificação completa (garante que está pronta para render)
              if (img.decode) {
                await img.decode();
              }

              // Pequeno delay para garantir que o browser está pronto para renderizar
              await new Promise((r) => setTimeout(r, 50));

              clearTimeout(timeoutId);
              resolve();
            } catch (error) {
              clearTimeout(timeoutId);
              resolve();
            }
          };

          img.onerror = () => {
            clearTimeout(timeoutId);
            resolve(); // Resolve mesmo com erro para não travar
          };

          img.src = url;
        });
      });

      // Aguarda todas as imagens carregarem (ou timeout)
      await Promise.all(loadPromises);

      // Sincroniza com múltiplos ciclos de renderização do browser
      // para garantir que as imagens estão no cache e prontas
      await new Promise((resolve) =>
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(resolve);
          });
        }),
      );

      // Delay maior para garantir que o browser processou e cacheou as imagens
      // Isso previne o flash de cor antes da imagem aparecer
      await new Promise((resolve) => setTimeout(resolve, 200));

      return true;
    },

    /**
     * Emite o background change após pré-carregar as imagens
     */
    async emitBackgroundChange(style) {
      if (!this.configLoaded) return;

      // Pré-carrega imagens antes de emitir
      await this.preloadBackgroundImages(style);

      this.$emit("background-change", style);
    },

    /**
     * Alterna entre modo dark e light e aplica as configurações do grupo correspondente
     */
    switchMode(newMode) {
      const currentDevice = this.formStyling.currentEditingDevice;
      const oldMode = this.formStyling[currentDevice].mode;

      // Se já está no modo solicitado, não faz nada
      if (oldMode === newMode) return;

      // Atualiza o modo
      this.formStyling[currentDevice].mode = newMode;

      // Aplica as configurações do grupo correspondente
      const groupKey = newMode === "dark" ? "darkMode" : "lightMode";
      this.applyGroupToConfig(groupKey, currentDevice, newMode);

      // Salva e emite as alterações
      this.saveBackgroundConfig();
      this.emitFormStyling();
    },

    /**
     * Sincroniza o modo interno do form styling quando o tema visual é alterado
     * Chamado pelo botão de tema no UserLogin
     */
    syncModeWithTheme(newMode) {
      const currentDevice = this.formStyling.currentEditingDevice;
      const oldMode = this.formStyling[currentDevice].mode;

      // Se já está no modo correto, não faz nada
      if (oldMode === newMode) {
        return;
      }

      // Atualiza o modo
      this.formStyling[currentDevice].mode = newMode;

      // Aplica as configurações do grupo correspondente
      const groupKey = newMode === "dark" ? "darkMode" : "lightMode";
      this.applyGroupToConfig(groupKey, currentDevice, newMode);

      // Salva e emite as alterações
      this.saveBackgroundConfig();
      this.emitFormStyling();
    },

    // ============ MÉTODOS DE GRUPOS DE CONFIGURAÇÃO ============

    /**
     * Aplica os grupos de configuração às configurações individuais
     * Ordem de prioridade: Configuração manual > Grupos específicos > Grupos globais
     */
    applyConfigGroups() {

      const devices = ["mobile", "tablet", "desktop"];

      // Para cada dispositivo, aplica os grupos respeitando o modo atual do dispositivo
      devices.forEach((device) => {
        // Cria configuração base se não existir
        if (!this.formStyling[device]) {
          this.formStyling[device] = { mode: "dark" };
        }

        // Obtém o modo atual deste dispositivo
        const currentMode = this.formStyling[device].mode || "dark";

        // Aplica grupos na ordem de prioridade (do menor para o maior)
        // 1. Grupos globais (menor prioridade)
        this.applyGroupToConfig("globalTexts", device, currentMode);

        // 2. Grupos específicos de modo (APENAS para o modo atual do dispositivo)
        if (currentMode === "dark") {
          this.applyGroupToConfig("darkMode", device, currentMode);
        } else {
          this.applyGroupToConfig("lightMode", device, currentMode);
        }

        // 3. Grupos específicos de dispositivo (maior prioridade dos grupos)
        if (device === "mobile") {
          this.applyGroupToConfig("mobileLayout", device, currentMode);
        } else if (device === "tablet") {
          this.applyGroupToConfig("tabletLayout", device, currentMode);
        } else if (device === "desktop") {
          this.applyGroupToConfig("desktopLayout", device, currentMode);
        }
      });

    },

    /**
     * Aplica um grupo específico a uma configuração de dispositivo/modo
     */
    applyGroupToConfig(groupKey, device, mode) {
      const group = this.configGroups[groupKey];

      if (!group) {
        return;
      }

      if (!group.enabled) {
        return;
      }

      // Verifica se o grupo deve ser aplicado a este dispositivo/modo
      if (!group.applyTo.devices.includes(device)) {
        return;
      }
      if (!group.applyTo.modes.includes(mode)) {
        return;
      }

      // Aplica os valores do grupo
      Object.keys(group.values).forEach((key) => {
        // customTexts precisa de merge individual por propriedade (cada prop é string)
        if (key === "customTexts" && typeof group.values[key] === "object") {
          // Inicializa se não existir
          if (!this.formStyling[device][key]) {
            this.formStyling[device][key] = {};
          }
          // Merge individual de cada texto
          Object.keys(group.values[key]).forEach((textKey) => {
            const existingValue = this.formStyling[device][key][textKey];
            const groupValue = group.values[key][textKey];

            // Se já existe como objeto {enabled, content} do backend, preserva a estrutura
            if (existingValue && typeof existingValue === 'object' && existingValue.content !== undefined) {
              // Apenas atualiza o content se for diferente, mantém enabled
              if (existingValue.content !== groupValue) {
                this.formStyling[device][key][textKey] = {
                  enabled: existingValue.enabled,
                  content: groupValue
                };
              }
            } else {
              // Não existe como objeto, cria string simples
              this.formStyling[device][key][textKey] = groupValue;
            }
          });
        }
        // Deep merge para outros objetos aninhados
        else if (
          typeof group.values[key] === "object" &&
          !Array.isArray(group.values[key])
        ) {
          this.formStyling[device][key] = {
            ...this.formStyling[device][key],
            ...JSON.parse(JSON.stringify(group.values[key])),
          };
        } else {
          this.formStyling[device][key] = group.values[key];
        }
      });
    },

    /**
     * Aplica um grupo específico e salva
     */
    applyGroup(groupKey) {
      const group = this.configGroups[groupKey];
      if (!group) return;

      this.applyConfigGroups();
      this.saveBackgroundConfig();
      this.emitFormStyling();

      this.$alert({
        icon: "success",
        title: "Grupo aplicado",
        text: `O grupo "${group.name}" foi aplicado com sucesso.`,
        timer: 2000,
        showConfirmButton: false,
      });
    },

    /**
     * Atualiza um valor dentro de um grupo
     */
    updateGroupValue(groupKey, valuePath, newValue) {
      const group = this.configGroups[groupKey];
      if (!group) return;

      const currentDevice = this.formStyling.currentEditingDevice;

      // Se estiver editando grupo Dark ou Light, sincroniza o modo do dispositivo atual
      // para garantir que o usuário veja as alterações visualmente
      if (groupKey === "darkMode") {
        if (this.formStyling[currentDevice].mode !== "dark") {
          this.formStyling[currentDevice].mode = "dark";
        }
      } else if (groupKey === "lightMode") {
        if (this.formStyling[currentDevice].mode !== "light") {
          this.formStyling[currentDevice].mode = "light";
        }
      }

      // Suporta nested paths como 'customTexts.preLoginTitle.enabled'
      const keys = valuePath.split(".");
      let target = group.values;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!target[keys[i]]) {
          target[keys[i]] = {};
        }
        target = target[keys[i]];
      }

      // Guard: se o valor não mudou, não faz nada (previne double-fire de @input)
      const currentValue = target[keys[keys.length - 1]];
      if (currentValue === newValue) return;

      target[keys[keys.length - 1]] = newValue;

      // Para grupos de aparência (dark/light), aplica diretamente ao dispositivo atual
      if (groupKey === "darkMode" || groupKey === "lightMode") {
        const mode = groupKey === "darkMode" ? "dark" : "light";
        this.applyGroupToConfig(groupKey, currentDevice, mode);
      } else {
        // Aplica APENAS o grupo específico aos dispositivos alvo (não reaplica todos)
        if (group.enabled) {
          group.applyTo.devices.forEach((device) => {
            const currentMode = this.formStyling[device]?.mode || "dark";
            if (group.applyTo.modes.includes(currentMode)) {
              this.applyGroupToConfig(groupKey, device, currentMode);
            }
          });
        }
      }

      this.saveBackgroundConfig();
      this.emitFormStyling();

    },

    /**
     * Toggle de dispositivo/modo em um grupo
     */
    toggleGroupApplyTo(groupKey, type, value) {
      const group = this.configGroups[groupKey];
      if (!group) return;

      const list =
        type === "device" ? group.applyTo.devices : group.applyTo.modes;
      const index = list.indexOf(value);

      if (index > -1) {
        list.splice(index, 1);
      } else {
        list.push(value);
      }

      this.applyConfigGroups();
      this.saveBackgroundConfig();
      this.emitFormStyling();
    },

    /**
     * Detecta padrões nas configurações atuais e atualiza os grupos
     */
    detectConfigPatterns() {

      // Detecta configurações específicas de modo dark/light
      const devices = ["mobile", "tablet", "desktop"];
      const colorProperties = [
        "buttonColorType1",
        "buttonColorType2",
        "buttonTextColor",
        "buttonHoverColor",
        "textColor",
        "inputTextColor",
        "inputBgEnabled",
        "inputBgDisabled",
      ];
      const layoutProperties = [
        "width",
        "minWidth",
        "maxWidth",
        "alignment",
        "alignmentOffset",
        "margin",
      ];

      devices.forEach((device) => {
        const config = this.formStyling[device];
        if (!config) return;

        const mode = config.mode || "dark";
        const modeGroupKey = mode === "dark" ? "darkMode" : "lightMode";
        const layoutGroupKey = `${device}Layout`;

        // Atualiza grupo de modo (dark/light)
        if (this.configGroups[modeGroupKey]) {
          this.configGroups[modeGroupKey].values.backgroundColor =
            config.backgroundColor;
          this.configGroups[modeGroupKey].values.opacity = config.opacity;
          this.configGroups[modeGroupKey].values.logoInvert = config.logoInvert;

          // Atualiza cores
          colorProperties.forEach((colorKey) => {
            if (config[colorKey] !== undefined) {
              this.configGroups[modeGroupKey].values[colorKey] = config[colorKey];
            }
          });
        }

        // Atualiza grupo de layout (mobile/tablet/desktop)
        if (this.configGroups[layoutGroupKey]) {
          layoutProperties.forEach((layoutKey) => {
            if (config[layoutKey] !== undefined) {
              this.configGroups[layoutGroupKey].values[layoutKey] = config[layoutKey];
            }
          });
        }

        // Atualiza textos customizados no grupo globalTexts
        if (config.customTexts && this.configGroups.globalTexts) {
          // Converte objetos {enabled, content} de volta para strings simples para o grupo
          Object.keys(config.customTexts).forEach((textKey) => {
            const textObj = config.customTexts[textKey];
            if (textObj && typeof textObj === 'object' && textObj.content !== undefined) {
              this.configGroups.globalTexts.values.customTexts[textKey] = textObj.content;
            } else if (typeof textObj === 'string') {
              this.configGroups.globalTexts.values.customTexts[textKey] = textObj;
            }
          });
        }
      });

    },

    /**
     * Toggle enabled state of a config group
     */
    toggleConfigGroupEnabled(groupKey) {
      if (this.configGroups[groupKey]) {
        this.configGroups[groupKey].enabled =
          !this.configGroups[groupKey].enabled;

        // Reaplica e emite em ambos os sentidos (ligar e desligar).
        // Sem o emit/save no "off", o CadastroInicio recebe
        // enabled[device]=true mesmo com o grupo desligado no painel.
        this.applyConfigGroups();
        this.saveBackgroundConfig();
        this.emitFormStyling();
      }
    },

    /**
     * Apply all enabled config groups
     */
    applyAllConfigGroups() {
      this.applyConfigGroups();
      this.saveBackgroundConfig();
      this.emitFormStyling();

      this.$alert({
        icon: "success",
        title: "Grupos aplicados",
        text: "Todos os grupos ativos foram aplicados com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      });
    },

    /**
     * Salva preferências de grupos no localStorage
     */
    saveGroupPreferences() {
      formStylingPreferences.saveGroupPreferences(this.configGroups);
    },

    /**
     * Carrega preferências de grupos do localStorage
     */
    loadGroupPreferences() {
      formStylingPreferences.loadGroupPreferences(this.configGroups);
    },

    // ============ MÉTODOS DE TEMPLATES ============

    /**
     * Carrega templates salvos do localStorage
     */
    loadSavedTemplates() {
      this.savedTemplates = formStylingPreferences.loadTemplates();
    },

    /**
     * Salva templates no localStorage
     */
    saveTemplatesToStorage() {
      formStylingPreferences.saveTemplates(this.savedTemplates);
    },

    /**
     * Abre modal para salvar novo template
     */
    openSaveTemplateModal() {
      this.templateAction = "save";
      this.newTemplateName = "";
      this.showTemplateModal = true;
    },

    /**
     * Abre modal para carregar template
     */
    openLoadTemplateModal() {
      this.templateAction = "load";
      this.selectedTemplate = null;
      this.showTemplateModal = true;
    },

    /**
     * Salva configurações atuais como template
     */
    saveTemplate() {
      if (!this.newTemplateName.trim()) {
        this.$alert({
          icon: "warning",
          title: "Nome necessário",
          text: "Por favor, digite um nome para o template.",
        });
        return;
      }

      const template = {
        id: Date.now(),
        name: this.newTemplateName.trim(),
        createdAt: new Date().toISOString(),
        configGroups: JSON.parse(JSON.stringify(this.configGroups)),
        preview: {
          darkModeEnabled: this.configGroups.darkMode?.enabled || false,
          lightModeEnabled: this.configGroups.lightMode?.enabled || false,
          primaryColor:
            this.configGroups.darkMode?.values?.buttonColorType1 || "#3b82f6",
        },
      };

      this.savedTemplates.push(template);
      this.saveTemplatesToStorage();
      this.showTemplateModal = false;

      this.$alert({
        icon: "success",
        title: "Template salvo",
        text: `Template "${template.name}" foi salvo com sucesso.`,
        timer: 2000,
        showConfirmButton: false,
      });

    },

    /**
     * Carrega um template salvo
     */
    loadTemplate(template) {
      if (!template) return;

      this.$alert({
        icon: "question",
        title: "Carregar template?",
        text: `Deseja carregar o template "${template.name}"? As configurações atuais serão substituídas.`,
        showCancelButton: true,
        confirmButtonText: "Sim, carregar",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.value) {

          // Carrega as configurações do template (deep clone)
          this.configGroups = JSON.parse(JSON.stringify(template.configGroups));

          // Força aplicação completa de todos os grupos ativos
          this.applyConfigGroups();

          // Aguarda um ciclo para garantir que Vue processou as mudanças
          this.$nextTick(() => {
            this.applyConfigGroups();

            // Salva configuração no servidor e localStorage
            this.saveBackgroundConfig();
            this.saveGroupPreferences();

            // Emite evento para atualizar componentes
            this.emitFormStyling();

            this.showTemplateModal = false;

            this.$alert({
              icon: "success",
              title: "Template carregado",
              text: `Template "${template.name}" foi aplicado com sucesso.`,
              timer: 2000,
              showConfirmButton: false,
            });

          });
        }
      });
    },

    /**
     * Deleta um template
     */
    deleteTemplate(templateId) {
      const template = this.savedTemplates.find((t) => t.id === templateId);
      if (!template) return;

      this.$alert({
        icon: "warning",
        title: "Deletar template?",
        text: `Deseja realmente deletar o template "${template.name}"? Esta ação não pode ser desfeita.`,
        showCancelButton: true,
        confirmButtonText: "Sim, deletar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#dc2626",
      }).then((result) => {
        if (result.value) {
          this.savedTemplates = this.savedTemplates.filter(
            (t) => t.id !== templateId,
          );
          this.saveTemplatesToStorage();

          this.$alert({
            icon: "success",
            title: "Template deletado",
            text: `Template "${template.name}" foi removido.`,
            timer: 2000,
            showConfirmButton: false,
          });

        }
      });
    },

    /**
     * Exporta template como arquivo JSON
     */
    exportTemplate(template) {
      const dataStr = JSON.stringify(template, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `template-${template.name
        .replace(/\s+/g, "-")
        .toLowerCase()}.json`;
      link.click();
      URL.revokeObjectURL(url);

    },

    /**
     * Importa template de arquivo JSON
     */
    importTemplate(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const template = JSON.parse(e.target.result);

          // Valida estrutura básica do template
          if (!template.name || !template.configGroups) {
            throw new Error("Template inválido");
          }

          // Adiciona novo ID e data
          template.id = Date.now();
          template.createdAt = new Date().toISOString();

          this.savedTemplates.push(template);
          this.saveTemplatesToStorage();

          this.$alert({
            icon: "success",
            title: "Template importado",
            text: `Template "${template.name}" foi importado com sucesso.`,
            timer: 2000,
            showConfirmButton: false,
          });

        } catch (error) {
          this.$alert({
            icon: "error",
            title: "Erro ao importar",
            text: "Arquivo de template inválido.",
          });
        }
      };
      reader.readAsText(file);

      // Reset input
      event.target.value = "";
    },

    /**
     * Formata data para exibição
     */
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>
